import { Draft } from 'immer';

import { AnyResourceDefinition } from '../../resources/types';
import { ResolverState } from '../../resolvers/definition/resolverState';
import {
  ON_ATTACH,
  ON_DATA,
  Link,
  LinkBehavior,
  TagWithContext,
  TagResult,
} from './link';
import {
  pushFromData,
  pullFromData,
  pushFromDataImmutable,
  pullFromDataImmutable,
} from './helpers';

// ─── Types ───────────────────────────────────────────────────────────

type MaybeContext<T> = T extends undefined ? {} : { context: T };

/**
 * Every tag matched between the two instances this change, with the context each
 * side contributed. Iterate this to join across all matches in one transform
 * (many-to-many links). For single-match links, `self.context` / `other.context`
 * — the first match — is the simpler read.
 */
type ContextPairs<TSelfContext, TOtherContext> = {
  self: TSelfContext;
  other: TOtherContext;
}[];

export type PushTransform<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> = (opts: {
  self: {
    data: TSelfData;
    params: TSelfParams;
  } & MaybeContext<TSelfContext>;
  other: {
    data?: Draft<TOtherData>;
    params: TOtherParams;
  } & MaybeContext<TOtherContext>;
  getContexts: () => ContextPairs<TSelfContext, TOtherContext>;
}) => TOtherData | void;

export type PullTransform<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> = (opts: {
  self: {
    data?: Draft<TSelfData>;
    params: TSelfParams;
  } & MaybeContext<TSelfContext>;
  other: {
    data: TOtherData;
    params: TOtherParams;
  } & MaybeContext<TOtherContext>;
  getContexts: () => ContextPairs<TSelfContext, TOtherContext>;
}) => TSelfData | void;

export type PushTransformImmutable<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> = (opts: {
  self: {
    data: TSelfData;
    params: TSelfParams;
  } & MaybeContext<TSelfContext>;
  other: {
    data?: TOtherData;
    params: TOtherParams;
  } & MaybeContext<TOtherContext>;
  getContexts: () => ContextPairs<TSelfContext, TOtherContext>;
}) => TOtherData | void;

export type PullTransformImmutable<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> = (opts: {
  self: {
    data?: TSelfData;
    params: TSelfParams;
  } & MaybeContext<TSelfContext>;
  other: {
    data: TOtherData;
    params: TOtherParams;
  } & MaybeContext<TOtherContext>;
  getContexts: () => ContextPairs<TSelfContext, TOtherContext>;
}) => TSelfData | void;

export type PushStateHandler<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> = (opts: {
  dataChanged: boolean;
  flagsChanged: boolean;
  self: {
    state: ResolverState<TSelfData>;
    params: TSelfParams;
  } & MaybeContext<TSelfContext>;
  other: {
    state?: ResolverState<TOtherData>;
    params: TOtherParams;
  } & MaybeContext<TOtherContext>;
}) => ResolverState<TOtherData> | void;

export type PullStateHandler<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> = (opts: {
  dataChanged: boolean;
  flagsChanged: boolean;
  self: {
    state?: ResolverState<TSelfData>;
    params: TSelfParams;
  } & MaybeContext<TSelfContext>;
  other: {
    state: ResolverState<TOtherData>;
    params: TOtherParams;
  } & MaybeContext<TOtherContext>;
}) => ResolverState<TSelfData> | void;

// ─── Discovery config types ─────────────────────────────────────────
// Shared by LinkBuilder and SelfLinkBuilder so the overload parameter
// shapes are defined in one place.

export type FindOtherByParams<TSelfParams, TOtherParams> = {
  fromSelfParams: (opts: {
    params: TSelfParams;
  }) => TOtherParams | TOtherParams[] | void;
  fromSelfData?: never;
};

export type FindOtherByData<TSelfParams, TSelfData, TOtherParams> = {
  fromSelfData: (opts: {
    data: TSelfData;
    params: TSelfParams;
  }) => TOtherParams | TOtherParams[] | void;
  fromSelfParams?: never;
};

export type FindOtherByDataCtx<TSelfParams, TSelfData, TOtherParams, TC> = {
  fromSelfData: (opts: {
    data: TSelfData;
    params: TSelfParams;
  }) => { params: TOtherParams; context: TC }[] | void;
  fromSelfParams?: never;
};

export type FindSelfByParams<TSelfParams, TOtherParams> = {
  fromOtherParams: (opts: {
    params: TOtherParams;
  }) => TSelfParams | TSelfParams[] | void;
  fromOtherData?: never;
};

export type FindSelfByData<TSelfParams, TOtherParams, TOtherData> = {
  fromOtherData: (opts: {
    data: TOtherData;
    params: TOtherParams;
  }) => TSelfParams | TSelfParams[] | void;
  fromOtherParams?: never;
};

export type FindSelfByDataCtx<TSelfParams, TOtherParams, TOtherData, TC> = {
  fromOtherData: (opts: {
    data: TOtherData;
    params: TOtherParams;
  }) => { params: TSelfParams; context: TC }[] | void;
  fromOtherParams?: never;
};

type TagSide<TParams, TData> =
  | {
      fromParams: (opts: { params: TParams }) => TagResult | TagResult[] | void;
      fromData?: never;
    }
  | {
      fromData: (opts: {
        data: TData;
        params: TParams;
      }) => TagResult | TagResult[] | void;
      fromParams?: never;
    };

type TagSideWithContext<TParams, TData, TContext> = {
  fromData: (opts: {
    data: TData;
    params: TParams;
  }) => TagWithContext<TContext> | TagWithContext<TContext>[] | void;
  fromParams?: never;
};

type TagConfig<TSelfParams, TSelfData, TOtherParams, TOtherData> = {
  self?: TagSide<TSelfParams, TSelfData>;
  other?: TagSide<TOtherParams, TOtherData>;
};

// ─── Builder phase interfaces ───────────────────────────────────────
//
// The builder is a type-state machine: methods are only visible when
// their prerequisites have been met. The phases are:
//
//   Initial  ──(findSelf/findOther/tags)──▶  Discovered
//   Discovered  ──(push/pushImmutable/pushState)──▶  PushSet
//   Discovered  ──(pull/pullImmutable/pullState)──▶  PullSet
//   PushSet  ──(pull/pullImmutable/pullState)──▶  Complete
//   PullSet  ──(push/pushImmutable/pushState)──▶  Complete
//
// pushBehavior, pullBehavior, and finalize are available on every
// post-discovery phase (via LinkBuilderFinalizable).

export interface LinkBuilderFinalizable<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
> {
  pushBehavior(behavior: Partial<LinkBehavior>): this;
  pullBehavior(behavior: Partial<LinkBehavior>): this;
  finalize(): Link<TSelfParams, TSelfData, TOtherParams, TOtherData>;
}

export interface LinkBuilderInitial<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
> {
  findSelf(config: {
    fromOtherParams: (opts: {
      params: TOtherParams;
    }) => TSelfParams | TSelfParams[] | void;
    fromOtherData?: never;
  }): LinkBuilderDiscovered<TSelfParams, TSelfData, TOtherParams, TOtherData>;

  findSelf(config: {
    fromOtherData: (opts: {
      data: TOtherData;
      params: TOtherParams;
    }) => TSelfParams | TSelfParams[] | void;
    fromOtherParams?: never;
  }): LinkBuilderDiscovered<TSelfParams, TSelfData, TOtherParams, TOtherData>;

  findSelf<TC>(config: {
    fromOtherData: (opts: {
      data: TOtherData;
      params: TOtherParams;
    }) => { params: TSelfParams; context: TC }[] | void;
    fromOtherParams?: never;
  }): LinkBuilderDiscovered<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    undefined,
    TC
  >;

  findOther(config: {
    fromSelfParams: (opts: {
      params: TSelfParams;
    }) => TOtherParams | TOtherParams[] | void;
    fromSelfData?: never;
  }): LinkBuilderDiscovered<TSelfParams, TSelfData, TOtherParams, TOtherData>;

  findOther(config: {
    fromSelfData: (opts: {
      data: TSelfData;
      params: TSelfParams;
    }) => TOtherParams | TOtherParams[] | void;
    fromSelfParams?: never;
  }): LinkBuilderDiscovered<TSelfParams, TSelfData, TOtherParams, TOtherData>;

  findOther<TC>(config: {
    fromSelfData: (opts: {
      data: TSelfData;
      params: TSelfParams;
    }) => { params: TOtherParams; context: TC }[] | void;
    fromSelfParams?: never;
  }): LinkBuilderDiscovered<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TC
  >;

  tags<TSC, TOC>(config: {
    self: TagSideWithContext<TSelfParams, TSelfData, TSC>;
    other: TagSideWithContext<TOtherParams, TOtherData, TOC>;
  }): LinkBuilderDiscovered<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSC,
    TOC
  >;

  tags<TSC>(config: {
    self: TagSideWithContext<TSelfParams, TSelfData, TSC>;
    other?: TagSide<TOtherParams, TOtherData>;
  }): LinkBuilderDiscovered<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSC
  >;

  tags<TOC>(config: {
    self?: TagSide<TSelfParams, TSelfData>;
    other: TagSideWithContext<TOtherParams, TOtherData, TOC>;
  }): LinkBuilderDiscovered<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    undefined,
    TOC
  >;

  tags(
    config: TagConfig<TSelfParams, TSelfData, TOtherParams, TOtherData>
  ): LinkBuilderDiscovered<TSelfParams, TSelfData, TOtherParams, TOtherData>;
}

export interface LinkBuilderDiscovered<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> extends LinkBuilderFinalizable<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData
  > {
  push(
    transform: PushTransform<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderPushSet<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSelfContext,
    TOtherContext
  >;

  pushImmutable(
    transform: PushTransformImmutable<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderPushSet<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSelfContext,
    TOtherContext
  >;

  pushState(
    handler: PushStateHandler<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderPushSet<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSelfContext,
    TOtherContext
  >;

  pull(
    transform: PullTransform<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderPullSet<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSelfContext,
    TOtherContext
  >;

  pullImmutable(
    transform: PullTransformImmutable<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderPullSet<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSelfContext,
    TOtherContext
  >;

  pullState(
    handler: PullStateHandler<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderPullSet<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSelfContext,
    TOtherContext
  >;
}

export interface LinkBuilderPushSet<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> extends LinkBuilderFinalizable<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData
  > {
  pull(
    transform: PullTransform<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderComplete<TSelfParams, TSelfData, TOtherParams, TOtherData>;

  pullImmutable(
    transform: PullTransformImmutable<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderComplete<TSelfParams, TSelfData, TOtherParams, TOtherData>;

  pullState(
    handler: PullStateHandler<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderComplete<TSelfParams, TSelfData, TOtherParams, TOtherData>;
}

export interface LinkBuilderPullSet<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> extends LinkBuilderFinalizable<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData
  > {
  push(
    transform: PushTransform<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderComplete<TSelfParams, TSelfData, TOtherParams, TOtherData>;

  pushImmutable(
    transform: PushTransformImmutable<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderComplete<TSelfParams, TSelfData, TOtherParams, TOtherData>;

  pushState(
    handler: PushStateHandler<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ): LinkBuilderComplete<TSelfParams, TSelfData, TOtherParams, TOtherData>;
}

export type LinkBuilderComplete<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
> = LinkBuilderFinalizable<TSelfParams, TSelfData, TOtherParams, TOtherData>;

// ─── Internal helpers ────────────────────────────────────────────────

const normalize = <T>(result: T | T[] | void): T[] => {
  if (result == null) {
    return [];
  }

  return Array.isArray(result) ? result : [result];
};

const hasContext = (
  result: unknown
): result is { params: unknown; context: unknown } =>
  typeof result === 'object' &&
  result !== null &&
  'params' in result &&
  'context' in result;

const behaviorString = (behavior: LinkBehavior) =>
  `(${behavior.invalidation ? 'I' : '-'}|${behavior.state ? 'S' : '-'})`;

const toLinkKey = ({
  selfTypeName,
  otherTypeName,
  pushBehavior,
  pullBehavior,
  timing,
}: Link<any, any, any, any>) =>
  `${timing}:${selfTypeName}${behaviorString(pushBehavior)}><${behaviorString(
    pullBehavior
  )}${otherTypeName}`;

// ─── Builder ─────────────────────────────────────────────────────────

export class LinkBuilder<
  TSelfParams,
  TSelfData,
  TOtherParams,
  TOtherData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> {
  private link: Link<TSelfParams, TSelfData, TOtherParams, TOtherData>;

  constructor(
    private selfResource: AnyResourceDefinition,
    private otherResource: AnyResourceDefinition
  ) {
    this.link = {
      key: '',
      selfTypeName: selfResource.typeName,
      otherTypeName: otherResource.typeName,
      timing: ON_ATTACH,
      pushBehavior: {
        state: false,
        invalidation: true,
      },
      pullBehavior: {
        state: false,
        invalidation: true,
      },
    };
  }

  /**
   * Find self using the other instance's params or data. Use when the other side
   * knows how to locate self rather than vice versa.
   *
   * @example
   * // Parent doesn't know its children, but children's params contain the parent id
   * Parent.linkTo(Child, b => b.findSelf({ fromOtherParams: ({ params }) => ({ id: params.parentId }) }))
   */
  findSelf(config: FindSelfByParams<TSelfParams, TOtherParams>): this;

  findSelf(config: FindSelfByData<TSelfParams, TOtherParams, TOtherData>): this;

  findSelf<TC>(
    config: FindSelfByDataCtx<TSelfParams, TOtherParams, TOtherData, TC>
  ): LinkBuilder<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSelfContext,
    TC
  >;

  findSelf(config: { fromOtherParams?: Function; fromOtherData?: Function }) {
    const { fromOtherParams: paramsFn, fromOtherData: dataFn } = config;

    if (dataFn) {
      this.link = {
        ...this.link,
        timing: ON_DATA,
        tagParams: {
          self: ({ params }) => [this.selfResource.identifyInstance(params)],
        },
        tagData: {
          other: ({ data, params }) => {
            const results = normalize(dataFn({ data, params }));
            return results.map((r: any) =>
              hasContext(r)
                ? ({
                    tag: this.selfResource.identifyInstance(r.params),
                    context: r.context,
                  } satisfies TagWithContext)
                : this.selfResource.identifyInstance(r)
            );
          },
        },
      };
    } else if (paramsFn) {
      this.link = {
        ...this.link,
        timing: ON_ATTACH,
        tagParams: {
          self: ({ params }) => [this.selfResource.identifyInstance(params)],
          other: ({ params }) =>
            normalize(paramsFn({ params })).map(
              this.selfResource.identifyInstance
            ),
        },
      };
    }

    return this as any;
  }

  /**
   * Find the other instance using self's params or data.
   *
   * Use `fromSelfParams` when self's params are enough to identify other (link is established on attach).
   * Use `fromSelfData` when you need self's resolved data to determine which other instances to link
   * (link is established after data loads). Return `{ params, context }[]` from `fromSelfData` to
   * attach typed context accessible via `self.context` in push/pull.
   *
   * @example
   * // PropertyValue knows its CrmObject from params alone
   * .findOther({ fromSelfParams: ({ params }) => ({ objectTypeId: params.objectTypeId, objectId: params.objectId }) })
   *
   * // CrmSearch discovers CrmObjects from response data
   * .findOther({ fromSelfData: ({ data }) => data.results.map(r => ({ objectTypeId: r.objectTypeId, objectId: r.objectId })) })
   */
  findOther(config: FindOtherByParams<TSelfParams, TOtherParams>): this;

  findOther(
    config: FindOtherByData<TSelfParams, TSelfData, TOtherParams>
  ): this;

  findOther<TC>(
    config: FindOtherByDataCtx<TSelfParams, TSelfData, TOtherParams, TC>
  ): LinkBuilder<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TC,
    TOtherContext
  >;

  findOther(config: { fromSelfParams?: Function; fromSelfData?: Function }) {
    const { fromSelfParams: paramsFn, fromSelfData: dataFn } = config;

    if (dataFn) {
      this.link = {
        ...this.link,
        timing: ON_DATA,
        tagParams: {
          other: ({ params }) => [this.otherResource.identifyInstance(params)],
        },
        tagData: {
          self: ({ data, params }) => {
            const results = normalize(dataFn({ data, params }));
            return results.map((r: any) =>
              hasContext(r)
                ? ({
                    tag: this.otherResource.identifyInstance(r.params),
                    context: r.context,
                  } satisfies TagWithContext)
                : this.otherResource.identifyInstance(r)
            );
          },
        },
      };
    } else if (paramsFn) {
      this.link = {
        ...this.link,
        timing: ON_ATTACH,
        tagParams: {
          self: ({ params }) =>
            normalize(paramsFn({ params })).map(
              this.otherResource.identifyInstance
            ),
          other: ({ params }) => [this.otherResource.identifyInstance(params)],
        },
      };
    }

    return this as any;
  }

  /**
   * Low-level tag-based instance matching. Both sides independently produce string tags;
   * instances are linked when their tags match. Use this when `findSelf`/`findOther` can't
   * express the relationship (e.g. many-to-many, or both sides need to produce tags from data).
   *
   * Return `{ tag, context }` from a `byData` callback to attach typed context
   * accessible via `self.context` / `other.context` in push/pull.
   *
   * @example
   * .tags({
   *   self:  { fromData: ({ data }) => data.items.map((item, i) => ({ tag: item.id, context: i })) },
   *   other: { fromData: ({ data }) => [data.id] },
   * })
   */
  tags<TSC, TOC>(config: {
    self: TagSideWithContext<TSelfParams, TSelfData, TSC>;
    other: TagSideWithContext<TOtherParams, TOtherData, TOC>;
  }): LinkBuilder<TSelfParams, TSelfData, TOtherParams, TOtherData, TSC, TOC>;

  tags<TSC>(config: {
    self: TagSideWithContext<TSelfParams, TSelfData, TSC>;
    other?: TagSide<TOtherParams, TOtherData>;
  }): LinkBuilder<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSC,
    TOtherContext
  >;

  tags<TOC>(config: {
    self?: TagSide<TSelfParams, TSelfData>;
    other: TagSideWithContext<TOtherParams, TOtherData, TOC>;
  }): LinkBuilder<
    TSelfParams,
    TSelfData,
    TOtherParams,
    TOtherData,
    TSelfContext,
    TOC
  >;

  tags(
    config: TagConfig<TSelfParams, TSelfData, TOtherParams, TOtherData>
  ): this;

  tags(config: {
    self?:
      | TagSide<TSelfParams, TSelfData>
      | TagSideWithContext<TSelfParams, TSelfData, any>;
    other?:
      | TagSide<TOtherParams, TOtherData>
      | TagSideWithContext<TOtherParams, TOtherData, any>;
  }) {
    const selfParams =
      config.self && 'fromParams' in config.self
        ? config.self.fromParams
        : undefined;
    const selfData =
      config.self && 'fromData' in config.self
        ? config.self.fromData
        : undefined;
    const otherParams =
      config.other && 'fromParams' in config.other
        ? config.other.fromParams
        : undefined;
    const otherData =
      config.other && 'fromData' in config.other
        ? config.other.fromData
        : undefined;

    const hasDataFns = selfData || otherData;

    const wrapTag = <T>(
      fn: ((opts: T) => TagResult | TagResult[] | void) | undefined
    ): ((opts: T) => TagResult[] | void) | undefined =>
      fn &&
      ((opts: T) => {
        const result = fn(opts);
        return result == null ? undefined : normalize(result);
      });

    this.link = {
      ...this.link,
      timing: hasDataFns ? ON_DATA : ON_ATTACH,
      tagParams: {
        self: wrapTag(selfParams),
        other: wrapTag(otherParams),
      },
      ...(hasDataFns
        ? {
            tagData: {
              self: wrapTag(selfData),
              other: wrapTag(otherData),
            },
          }
        : {}),
    };

    return this;
  }

  /**
   * When self's data changes, update other's data. Only fires when self has data
   * and the data actually changed.
   *
   * `other.data` is an Immer draft of other's current data: either mutate it in place
   * and return nothing, or return a brand-new value. Returning void without mutating
   * the draft skips the update. (The draft is only valid synchronously inside the
   * transform — don't stash it.)
   *
   * @example
   * // mutate the draft of other's data
   * .push(({ self, other }) => {
   *   if (other.data) {
   *     other.data.items.push(self.data);
   *   }
   * })
   *
   * // or return a fresh value
   * .push(({ self, other }) => self.data.items.find(i => i.id === other.params.id))
   */
  push(
    transform: PushTransform<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ) {
    this.link = {
      ...this.link,
      pushBehavior: { invalidation: false, state: true },
      onPush: pushFromData(transform as any),
    };
    return this;
  }

  /**
   * When other's data changes, update self's data. The reverse of `push` — only fires
   * when other has data and the data actually changed.
   *
   * `self.data` is an Immer draft of self's current data: either mutate it in place
   * and return nothing, or return a brand-new value. Returning void without mutating
   * the draft skips the update. (The draft is only valid synchronously inside the
   * transform — don't stash it.)
   *
   * @example
   * // mutate the draft of self's data
   * .pull(({ self, other }) => {
   *   const item = self.data?.find(i => i.id === other.params.id);
   *   if (item) {
   *     Object.assign(item, other.data);
   *   }
   * })
   *
   * // or return a fresh value
   * .pull(({ self, other }) => self.data && self.data.map(i => i.id === other.params.id ? other.data : i))
   */
  pull(
    transform: PullTransform<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ) {
    this.link = {
      ...this.link,
      pullBehavior: { invalidation: false, state: true },
      onPull: pullFromData(transform as any),
    };
    return this;
  }

  /**
   * Like `push`, but return-only: the transform must return the new value rather
   * than mutate. The immutable counterpart used when the target's data type can't
   * be expressed as a mutable draft, or to opt out of drafting entirely.
   */
  pushImmutable(
    transform: PushTransformImmutable<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ) {
    this.link = {
      ...this.link,
      pushBehavior: { invalidation: false, state: true },
      onPush: pushFromDataImmutable(transform as any),
    };
    return this;
  }

  /** Like `pull`, but return-only. The immutable counterpart to `pushImmutable`. */
  pullImmutable(
    transform: PullTransformImmutable<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ) {
    this.link = {
      ...this.link,
      pullBehavior: { invalidation: false, state: true },
      onPull: pullFromDataImmutable(transform as any),
    };
    return this;
  }

  /**
   * Like `push`, but operates on the full `ResolverState` (loading, error, stale flags)
   * instead of just data. Use when you need to propagate loading/error states or
   * react to flag changes, not just data changes.
   */
  pushState(
    handler: PushStateHandler<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ) {
    this.link = {
      ...this.link,
      pushBehavior: { invalidation: false, state: true },
      onPush: handler as any,
    };
    return this;
  }

  /** Like `pull`, but operates on the full `ResolverState`. */
  pullState(
    handler: PullStateHandler<
      TSelfParams,
      TSelfData,
      TOtherParams,
      TOtherData,
      TSelfContext,
      TOtherContext
    >
  ) {
    this.link = {
      ...this.link,
      pullBehavior: { invalidation: false, state: true },
      onPull: handler as any,
    };
    return this;
  }

  pushBehavior(behavior: Partial<LinkBehavior>) {
    this.link = {
      ...this.link,
      pushBehavior: {
        ...this.link.pushBehavior,
        ...behavior,
      },
    };
    return this;
  }

  pullBehavior(behavior: Partial<LinkBehavior>) {
    this.link = {
      ...this.link,
      pullBehavior: {
        ...this.link.pullBehavior,
        ...behavior,
      },
    };
    return this;
  }

  finalize(): Link<TSelfParams, TSelfData, TOtherParams, TOtherData> {
    const isSelfLink = this.link.selfTypeName === this.link.otherTypeName;

    if (isSelfLink && this.link.onPush && !this.link.onPull) {
      const onPush = this.link.onPush;
      this.link.pullBehavior = { ...this.link.pushBehavior };
      this.link.onPull = ((opts: any) =>
        onPush({
          ...opts,
          self: opts.other,
          other: opts.self,
          getContexts: () =>
            opts.getContexts().map((c: any) => ({
              self: c.other,
              other: c.self,
            })),
        })) as any;
    }

    if (this.link.pushBehavior.state && !this.link.onPush) {
      throw Error('Push transform must be defined for data-type link');
    }

    if (this.link.pullBehavior.state && !this.link.onPull) {
      throw Error('Pull transform must be defined for data-type reverse link');
    }

    this.link.key = toLinkKey(this.link);

    return this.link as Link<TSelfParams, TSelfData, TOtherParams, TOtherData>;
  }
}

// ─── SelfLinkBuilder ────────────────────────────────────────────────
// Type-level restriction for self-links: only discovery + sync + behavior.
// At runtime this is still a LinkBuilder instance — the interface just
// narrows what's visible from the linkTo(SELF) callback.

export interface SelfLinkBuilder<
  TParams,
  TData,
  TSelfContext = undefined,
  TOtherContext = undefined,
> {
  findOther(
    config: FindOtherByParams<TParams, TParams>
  ): SelfLinkBuilder<TParams, TData, TSelfContext, TOtherContext>;

  findOther(
    config: FindOtherByData<TParams, TData, TParams>
  ): SelfLinkBuilder<TParams, TData, TSelfContext, TOtherContext>;

  findOther<TC>(
    config: FindOtherByDataCtx<TParams, TData, TParams, TC>
  ): SelfLinkBuilder<TParams, TData, TC, TOtherContext>;

  findSelf(
    config: FindSelfByParams<TParams, TParams>
  ): SelfLinkBuilder<TParams, TData, TSelfContext, TOtherContext>;

  findSelf(
    config: FindSelfByData<TParams, TParams, TData>
  ): SelfLinkBuilder<TParams, TData, TSelfContext, TOtherContext>;

  findSelf<TC>(
    config: FindSelfByDataCtx<TParams, TParams, TData, TC>
  ): SelfLinkBuilder<TParams, TData, TSelfContext, TC>;

  tags<TSC, TOC>(config: {
    self: TagSideWithContext<TParams, TData, TSC>;
    other: TagSideWithContext<TParams, TData, TOC>;
  }): SelfLinkBuilder<TParams, TData, TSC, TOC>;

  tags<TSC>(config: {
    self: TagSideWithContext<TParams, TData, TSC>;
    other?: TagSide<TParams, TData>;
  }): SelfLinkBuilder<TParams, TData, TSC, TOtherContext>;

  tags<TOC>(config: {
    self?: TagSide<TParams, TData>;
    other: TagSideWithContext<TParams, TData, TOC>;
  }): SelfLinkBuilder<TParams, TData, TSelfContext, TOC>;

  tags(
    config: TagConfig<TParams, TData, TParams, TData>
  ): SelfLinkBuilder<TParams, TData, TSelfContext, TOtherContext>;

  push(
    transform: PushTransform<
      TParams,
      TData,
      TParams,
      TData,
      TSelfContext,
      TOtherContext
    >
  ): SelfLinkBuilder<TParams, TData, TSelfContext, TOtherContext>;

  pushImmutable(
    transform: PushTransformImmutable<
      TParams,
      TData,
      TParams,
      TData,
      TSelfContext,
      TOtherContext
    >
  ): SelfLinkBuilder<TParams, TData, TSelfContext, TOtherContext>;

  pushBehavior(
    behavior: Partial<LinkBehavior>
  ): SelfLinkBuilder<TParams, TData, TSelfContext, TOtherContext>;

  finalize(): Link<TParams, TData, TParams, TData>;
}
